import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { IPlanBoostCompany, isPlanBoostActive, isPlanBoostEndingSoon } from '@vacationtracker/shared/functions/plan-boost'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'

const MINUTE_IN_MS = 60 * 1000

export interface IPlanBoostBannerState {
  daysLeft: number
  isEndingSoon: boolean
}

const getPlanBoostBannerState = (company: IPlanBoostCompany | null, now: Date): IPlanBoostBannerState | null => {
  const expiresAt = company?.planBoost?.expiresAt
  if (!expiresAt || !isPlanBoostActive(company, now)) {
    return null
  }
  return {
    daysLeft: dayjs(expiresAt).startOf('day').diff(dayjs(now).startOf('day'), 'day'),
    isEndingSoon: isPlanBoostEndingSoon(company, now),
  }
}

const isSameBannerState = (previous: IPlanBoostBannerState | null, next: IPlanBoostBannerState | null): boolean =>
  previous?.daysLeft === next?.daysLeft && previous?.isEndingSoon === next?.isEndingSoon

// MainApp renders the whole app, so the state only changes when what the banner shows changes, not on every tick
export const usePlanBoostBanner = (role: string): IPlanBoostBannerState | null => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const company = role === 'Admin' ? authCompany : null
  const [bannerState, setBannerState] = useState(() => getPlanBoostBannerState(company, new Date()))

  useEffect(() => {
    const update = (): boolean => {
      const next = getPlanBoostBannerState(company, new Date())
      setBannerState((previous) => (isSameBannerState(previous, next) ? previous : next))
      return next !== null
    }
    if (!update()) return

    const intervalId = setInterval(() => {
      if (!update()) clearInterval(intervalId)
    }, MINUTE_IN_MS)
    return () => clearInterval(intervalId)
  }, [company])

  return bannerState
}
